import { uiText, statusText } from '@/i18n/interface';

import jsPDF from 'jspdf';
import { TaxReportData, ReportPeriod } from '@/types/taxReport';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { addGreekFontSupport, addTextToPDF, addBilingualTextLine, addBilingualHeader, getBilingualText } from '@/utils/pdfGreekSupport';
import i18n from '@/i18n/config';

export const generateTaxReportPdf = async (
  reportData: TaxReportData,
  reportPeriod: ReportPeriod,
  dojoName: string,
  defaultCurrency: string
): Promise<void> => {
  const pdf = new jsPDF();

  // Add proper font support
  await addGreekFontSupport(pdf);

  const currencySymbol = getCurrencySymbol(defaultCurrency);
  const currentLanguage = i18n.language;

  // Helper function to format date as dd.mm.yyyy
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  let currentY = 30;

  try {
    // Header
    const headerText = getBilingualText('Tax Report', currentLanguage);
    currentY = addTextToPDF(pdf, `${headerText} - ${dojoName}`, 20, currentY, 20, 'bold');
    currentY += 15;

    currentY = addBilingualTextLine(pdf, 'Period', `${formatDate(reportPeriod.startDate)} to ${formatDate(reportPeriod.endDate)}`, 20, currentY, currentLanguage, 12);
    currentY += 10;

    currentY = addBilingualTextLine(pdf, 'Currency', defaultCurrency, 20, currentY, currentLanguage, 12);
    currentY += 10;

    currentY = addBilingualTextLine(pdf, 'Generated On', formatDate(new Date().toISOString()), 20, currentY, currentLanguage, 12);
    currentY += 20;

    // Summary section
    currentY = addBilingualHeader(pdf, 'Summary', 20, currentY, currentLanguage, 16);
    currentY += 15;

    currentY = addBilingualTextLine(pdf, 'Total Income', `${currencySymbol}${reportData.totalIncome.toFixed(2)}`, 20, currentY, currentLanguage, 12);
    currentY += 10;

    currentY = addBilingualTextLine(pdf, 'Total Expenses', `${currencySymbol}${reportData.totalExpenses.toFixed(2)}`, 20, currentY, currentLanguage, 12);
    currentY += 10;

    currentY = addBilingualTextLine(pdf, 'Net Income', `${currencySymbol}${reportData.netIncome.toFixed(2)}`, 20, currentY, currentLanguage, 12);
    currentY += 20;

    // Income by Category
    currentY = addBilingualHeader(pdf, 'Income by Category', 20, currentY, currentLanguage, 14);
    currentY += 15;

    for (const [category, amount] of Object.entries(reportData.incomeByCategory)) {
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }
      currentY = addTextToPDF(pdf, `${category}: ${currencySymbol}${amount.toFixed(2)}`, 20, currentY, 11);
      currentY += 10;
    }

    // Expenses by Category
    currentY += 10;
    if (currentY > 250) {
      pdf.addPage();
      currentY = 20;
    }

    currentY = addBilingualHeader(pdf, 'Expenses by Category', 20, currentY, currentLanguage, 14);
    currentY += 15;

    for (const [category, amount] of Object.entries(reportData.expensesByCategory)) {
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }
      currentY = addTextToPDF(pdf, `${category}: ${currencySymbol}${amount.toFixed(2)}`, 20, currentY, 11);
      currentY += 10;
    }

    // Detailed Transactions
    pdf.addPage();
    currentY = 20;

    currentY = addBilingualHeader(pdf, 'Detailed Transactions', 20, currentY, currentLanguage, 14);
    currentY += 20;

    // Table headers
    const dateHeader = getBilingualText('Date', currentLanguage);
    const typeHeader = getBilingualText('Type', currentLanguage);
    const categoryHeader = getBilingualText('Category', currentLanguage);
    const amountHeader = getBilingualText('Amount', currentLanguage);

    currentY = addTextToPDF(pdf, dateHeader, 20, currentY, 10);
    addTextToPDF(pdf, typeHeader, 50, currentY, 10);
    addTextToPDF(pdf, categoryHeader, 80, currentY, 10);
    addTextToPDF(pdf, amountHeader, 160, currentY, 10);
    currentY += 10;

    // Sort transactions by date and render
    const sortedTransactions = reportData.transactions.sort((a, b) => {
      const dateA = a.date.split('.').reverse().join('-');
      const dateB = b.date.split('.').reverse().join('-');
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    for (const transaction of sortedTransactions) {
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }

      addTextToPDF(pdf, transaction.date, 20, currentY, 10);
      addTextToPDF(pdf, statusText(transaction.type), 50, currentY, 10);
      addTextToPDF(pdf, transaction.category, 80, currentY, 10);
      addTextToPDF(pdf, `${currencySymbol}${transaction.amount.toFixed(2)}`, 160, currentY, 10);
      currentY += 8;
    }

    // Save the PDF
    const startDate = formatDate(reportPeriod.startDate);
    const endDate = formatDate(reportPeriod.endDate);
    pdf.save(`tax-report-${startDate}-to-${endDate}.pdf`);

  } catch (error) {
    console.error('Error generating PDF:', error);
    // Fallback to basic PDF generation
    await generateBasicTaxReportPdf(reportData, reportPeriod, dojoName, defaultCurrency);
  }
};

// Fallback function for basic PDF generation
const generateBasicTaxReportPdf = async (
  reportData: TaxReportData,
  reportPeriod: ReportPeriod,
  dojoName: string,
  defaultCurrency: string
) => {
  const pdf = new jsPDF();
  await addGreekFontSupport(pdf);

  const currencySymbol = getCurrencySymbol(defaultCurrency);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  let yPos = 30;

  // Basic fallback rendering in English only
  pdf.setFontSize(20);
  pdf.text(uiText("Tax Report for {{value0}}", { value0: dojoName }), 20, yPos);
  yPos += 20;

  pdf.setFontSize(12);
  pdf.text(uiText("Period: {{value0}} to {{value1}}", { value0: formatDate(reportPeriod.startDate), value1: formatDate(reportPeriod.endDate) }), 20, yPos);
  yPos += 10;

  pdf.text(uiText("Currency: {{value0}}", { value0: defaultCurrency }), 20, yPos);
  yPos += 20;

  pdf.text(uiText("Total Income: {{value0}}{{value1}}", { value0: currencySymbol, value1: reportData.totalIncome.toFixed(2) }), 20, yPos);
  yPos += 10;

  pdf.text(uiText("Total Expenses: {{value0}}{{value1}}", { value0: currencySymbol, value1: reportData.totalExpenses.toFixed(2) }), 20, yPos);
  yPos += 10;

  pdf.text(uiText("Net Income: {{value0}}{{value1}}", { value0: currencySymbol, value1: reportData.netIncome.toFixed(2) }), 20, yPos);

  const startDate = formatDate(reportPeriod.startDate);
  const endDate = formatDate(reportPeriod.endDate);
  pdf.save(`tax-report-${startDate}-to-${endDate}.pdf`);
};
